// Shared markdown loader + renderer for the `docs/logic/` build-doc corpus.
// Used by:
//   - src/design-system/HelpAnchor.tsx (the `?` drawer next to components)
//   - src/versions/v1/modules/docs-library (the /docs/logic reference tab)
// Cross-version because the corpus lives outside any single frozen version.

import { Marked } from 'marked';
import { parseFrontmatter } from './frontmatter';

// Slugify rule mirrors scripts/check-help-anchors.mjs and the Worker's
// insert.ts so heading IDs line up across browser, build check, and server.
export function slugifyHeading(text: string): string {
  return text
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/\s+/g, '-');
}

// Local Marked instance — keeps the global `marked` (used by ChangelogScene)
// untouched.
const md = new Marked();

// Rewrite GitHub-flavoured admonition blockquotes (`> [!note] author · date`
// followed by `> body lines`) into a styled `<div class="md-callout">` so
// they render with a left-rail accent and a kicker header instead of as a
// plain blockquote with the literal `[!note]` token visible.
//
// Marked accepts mixed HTML + markdown, so the body content inside the div
// is left as markdown and gets normal rendering.
function preprocessCallouts(source: string): string {
  return source.replace(
    /^>\s*\[!(\w+)\](?:[\t ]+([^\n]*))?\n((?:>[ \t]?[^\n]*(?:\n|$))*)/gm,
    (_, kind: string, header: string | undefined, body: string) => {
      const inner = body
        .split('\n')
        .map((line) => line.replace(/^>[ \t]?/, ''))
        .join('\n')
        .replace(/\s+$/, '');
      const kindLower = kind.toLowerCase();
      const kindLabel = kindLower.charAt(0).toUpperCase() + kindLower.slice(1);
      const headerText = header ? header.trim() : '';
      const head = headerText ? `${kindLabel} · ${headerText}` : kindLabel;
      return [
        `<div class="md-callout md-callout--${kindLower}">`,
        `<div class="md-callout__head">${head}</div>`,
        '',
        inner,
        '',
        '</div>',
        '',
      ].join('\n');
    },
  );
}

// Inject `<a id="...">` anchors immediately before each ATX heading so that
// `#<slug>` deep-links resolve via getElementById regardless of nesting.
// YAML frontmatter (Phase E of the 4-layer migration) is stripped before
// rendering so the metadata block doesn't appear as a visible code block;
// the HelpAnchor drawer surfaces those fields separately via parseFrontmatter.
export function renderBuildDoc(source: string): string {
  const { body } = parseFrontmatter(source);
  const withCallouts = preprocessCallouts(body);
  const withAnchors = withCallouts.replace(
    /^(#{1,6})\s+(.+)$/gm,
    (_, hashes, text) => {
      const slug = slugifyHeading(text);
      return `<a id="${slug}" class="help-anchor-target"></a>\n\n${hashes} ${text}`;
    },
  );
  return md.parse(withAnchors, { async: false }) as string;
}

export const BUILD_DOC_LOADERS = import.meta.glob(
  [
    '/docs/logic/**/*.md',
    '!/docs/logic/TEMPLATE.md',
    '!/docs/logic/README.md',
    '!/docs/logic/**/index.md',
    '!/docs/logic/index.md',
  ],
  { query: '?raw', import: 'default' },
) as Record<string, () => Promise<string>>;

// "studies/action-tracker", "reporting/report-view", … — newest list, alphabetical.
export function buildDocKeys(): string[] {
  return Object.keys(BUILD_DOC_LOADERS)
    .map((p) => p.replace(/^\/docs\/logic\//, '').replace(/\.md$/, ''))
    .sort();
}

export function loaderForBuildDoc(key: string): (() => Promise<string>) | undefined {
  return BUILD_DOC_LOADERS[`/docs/logic/${key}.md`];
}

// First path segment of the doc key, capitalised. "studies/action-tracker" → "Studies".
export function moduleForBuildDoc(key: string): string {
  const folder = key.split('/')[0] ?? '';
  return folder.charAt(0).toUpperCase() + folder.slice(1);
}

// Group doc keys by their capitalised module name. Used by the library sidebar.
export function groupBuildDocsByModule(): Record<string, string[]> {
  const groups: Record<string, string[]> = {};
  for (const key of buildDocKeys()) {
    const mod = moduleForBuildDoc(key);
    (groups[mod] ??= []).push(key);
  }
  return groups;
}

// Sub-group machinery — for modules whose doc count justifies a second tier,
// project keys into named sub-groups. Modules without an entry here render
// flat (no sub-grouping noise on small modules). The match for each
// sub-group is a `keysIncluded` predicate against the doc slug (the part
// after the module prefix); the first matching sub-group wins.
//
// Empty in the foundation — add sub-group definitions here when a module
// crosses the ~5-doc threshold and reads as "too flat" in the sidebar.
type SubGroupDef = {
  label: string;
  matches: (slug: string) => boolean;
};

const SUB_GROUP_DEFS: Record<string, SubGroupDef[]> = {};

export type DocSubGroup = {
  /** Display label, e.g. "Composition" / "Quality". */
  label: string;
  /** Full doc keys included (e.g. "reporting/writer-engine"). */
  keys: string[];
};

/**
 * For a module with `keys` doc paths, partition into sub-groups when the
 * module has a `SUB_GROUP_DEFS` entry. Returns null when the module has no
 * defined sub-groups — caller should render the flat list.
 *
 * Docs that don't match any sub-group rule land in an implicit trailing
 * "Other" group; in practice this should stay empty (audit if it ever
 * populates — usually a missing rule).
 */
export function subGroupsFor(
  module: string,
  keys: string[],
): DocSubGroup[] | null {
  const defs = SUB_GROUP_DEFS[module];
  if (!defs) return null;
  const remaining = new Set(keys);
  const groups: DocSubGroup[] = [];
  for (const def of defs) {
    const matched: string[] = [];
    for (const key of keys) {
      const slug = key.slice(module.length + 1).toLowerCase();
      // key like "reporting/writer-engine" → module "Reporting" → slug
      // "writer-engine". We compare lowercase here because module names are
      // capitalised but doc paths are lowercase.
      if (remaining.has(key) && def.matches(slug)) {
        matched.push(key);
        remaining.delete(key);
      }
    }
    if (matched.length > 0) {
      groups.push({ label: def.label, keys: matched });
    }
  }
  if (remaining.size > 0) {
    groups.push({ label: 'Other', keys: [...remaining] });
  }
  return groups;
}
